import { useEffect, useRef, useState } from "react";

export interface SearchResult {
	id: string;
	name: string;
	description: string | null;
	status: string | null;
	rank: number;
}

export interface FileSearchResult {
	id: string;
	name: string;
	contentType: string;
	rank: number;
}

export interface UserSearchResult {
	id: string;
	name: string;
	email: string;
	rank: number;
}

export interface MultiSearchResults {
	projects: SearchResult[];
	files: FileSearchResult[];
	members: UserSearchResult[];
}

const emptyResults: MultiSearchResults = {
	projects: [],
	files: [],
	members: [],
};

interface UseSearchReturn {
	query: string;
	setQuery: (q: string) => void;
	results: MultiSearchResults;
	loading: boolean;
	error: string | null;
}

export function useSearch(workspaceSlug: string | undefined): UseSearchReturn {
	const [query, setQuery] = useState("");
	const [results, setResults] = useState<MultiSearchResults>(emptyResults);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const abortRef = useRef<AbortController | null>(null);

	useEffect(() => {
		if (query.length < 2) {
			setResults(emptyResults);
			setLoading(false);
			setError(null);
			return;
		}

		setLoading(true);

		const timer = setTimeout(async () => {
			abortRef.current?.abort();
			const controller = new AbortController();
			abortRef.current = controller;

			try {
				const res = await fetch(
					`/api/tenant/${workspaceSlug}/search?q=${encodeURIComponent(query)}`,
					{
						credentials: "include",
						signal: controller.signal,
					},
				);
				if (!res.ok) {
					const body = await res.json().catch(() => null);
					setError(
						(body as { error?: string } | null)?.error ??
							`Search failed (${res.status})`,
					);
					setResults(emptyResults);
					return;
				}
				const json = (await res.json()) as MultiSearchResults;
				setResults(json);
				setError(null);
			} catch (err) {
				if (err instanceof DOMException && err.name === "AbortError") return;
				setError(err instanceof Error ? err.message : "Unknown error");
				setResults(emptyResults);
			} finally {
				if (!controller.signal.aborted) {
					setLoading(false);
				}
			}
		}, 300);

		return () => {
			clearTimeout(timer);
			abortRef.current?.abort();
		};
	}, [query, workspaceSlug]);

	return { query, setQuery, results, loading, error };
}
